import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SHA256 from 'crypto-js/sha256';
import { AppShell } from '../components/layout/AppShell';
import { ProgressBar } from '../components/ui/ProgressBar';
import { EmailModal } from '../components/modals/EmailModal';
import { FixCodeModal } from '../components/modals/FixCodeModal';
import { useScanStore } from '../store/scan';
import { useAuthStore } from '../store/auth';
import { generateProfessionalPDF } from '../utils/pdf-generator';
import { reportsAPI } from '../api/reports';
import client from '../api/client';
import { applyPatches } from '../utils/code-fixer';
import { scanCodeLocal } from '../utils/pattern-engine';
import { generateAIPluginReport, validateAISpecializedFileSet, validateAISpecializedSingleFile, } from '../utils/ai-plugin-detector';
import { ScanWorkspacePanel } from '../components/scan/ScanWorkspacePanel';
import { ScanResultsPanel } from '../components/scan/ScanResultsPanel';
import { ScanContextPanel } from '../components/scan/ScanContextPanel';
import { ScanChatPanel } from '../components/scan/ScanChatPanel';
// Utility function to generate SHA-256 hash
const generateSHA256 = (content) => {
    return SHA256(content).toString();
};
const getPrimaryWorkspaceContent = (files) => {
    const preferred = files.find((file) => file.content && file.content.trim().length > 0);
    return preferred?.content || files[0]?.content || '';
};
const inferPastedFileName = (content) => {
    const trimmed = content.trim();
    const timestamp = Date.now();
    if (/^#{1,6}\s+.*\bskill\b/im.test(trimmed) || /\bskill[_-]?(?:name|id|definition|manifest)\b/i.test(trimmed)) {
        return `SKILL_${timestamp}.md`;
    }
    if (/^#{1,6}\s+.*\bagent\b/im.test(trimmed) || /\bagent[_-]?(?:name|id|definition|manifest)\b/i.test(trimmed)) {
        return `AGENT_${timestamp}.md`;
    }
    if (/\bmcp\b/i.test(trimmed) || /\bmodel[_-]?context[_-]?protocol\b/i.test(trimmed)) {
        return `mcp_${timestamp}.yaml`;
    }
    if (/^\s*[\[{]/.test(trimmed)) {
        return `ai_asset_${timestamp}.json`;
    }
    if (/^\s*[A-Za-z0-9_-]+\s*:\s*.+/m.test(trimmed)) {
        return `ai_asset_${timestamp}.yaml`;
    }
    if (/^#{1,6}\s+/m.test(trimmed) || /```/.test(trimmed)) {
        return `ai_asset_${timestamp}.md`;
    }
    return `pasted_code_${timestamp}.txt`;
};
export const ScanPage = () => {
    const location = useLocation();
    const { currentScan, setCurrentScan, addToHistory, isScanning, setIsScanning, setError, error, pendingGitHubImport, setPendingGitHubImport, } = useScanStore();
    const { isAuthenticated, token, tenant } = useAuthStore();
    const isEnterprise = tenant?.tier === 'ENTERPRISE';
    const [fileContent, setFileContent] = useState('');
    const [fileName, setFileName] = useState('');
    const [isDecompressing, setIsDecompressing] = useState(false);
    const [extractedFiles, setExtractedFiles] = useState([]);
    const [archiveStructure, setArchiveStructure] = useState(null);
    const [showChat, setShowChat] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [reportEmail, setReportEmail] = useState(null);
    const [inputMode, setInputMode] = useState('upload');
    const [pastedCode, setPastedCode] = useState('');
    const [showFixCodeModal, setShowFixCodeModal] = useState(false);
    const [fixedCode, setFixedCode] = useState('');
    const [multiFileFixes, setMultiFileFixes] = useState([]);
    const [currentFixIndex, setCurrentFixIndex] = useState(0);
    const [archiveExtension, setArchiveExtension] = useState('');
    const [scanProgress, setScanProgress] = useState(0);
    const [isScanningVisible, setIsScanningVisible] = useState(false);
    const [expandedFindings, setExpandedFindings] = useState(new Set());
    const [detectedAIPlugins, setDetectedAIPlugins] = useState(null);
    const [aiPluginReport, setAIPluginReport] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [showSupportedFormats, setShowSupportedFormats] = useState(true);
    const [githubImportNotice, setGithubImportNotice] = useState(null);
    const syncArchiveCredits = async (files) => {
        if (!isAuthenticated || !token || files.length === 0) {
            return { attempted: 0, failed: 0, newScans: 0, reusedScans: 0, firstError: null };
        }
        let attempted = 0;
        let failed = 0;
        let newScans = 0;
        let reusedScans = 0;
        let firstError = null;
        for (const file of files) {
            attempted += 1;
            try {
                const scanResponse = await client.post('/scan', {
                    fileName: file.name,
                    fileContent: file.content,
                }).catch((err) => {
                    if (!firstError) {
                        firstError =
                            err?.response?.data?.error?.message ||
                                err?.response?.data?.message ||
                                `Credit sync request failed`;
                    }
                    failed += 1;
                    return null;
                });
                if (!scanResponse)
                    continue;
                const isNew = scanResponse.data?.data?.isNew;
                if (isNew === false) {
                    reusedScans += 1;
                }
                else {
                    newScans += 1;
                }
            }
            catch (err) {
                if (!firstError) {
                    firstError = err?.message || 'Credit sync request failed';
                }
                failed += 1;
            }
        }
        return { attempted, failed, newScans, reusedScans, firstError };
    };
    const getAvailableCredits = async () => {
        if (!isAuthenticated || !token) {
            return null;
        }
        try {
            const response = await client.get('/credits/balance');
            const balance = response.data?.data;
            if (typeof balance?.total_credits === 'number') {
                return Math.max(0, Math.floor(balance.total_credits));
            }
            if (typeof balance?.current_credits === 'number' &&
                typeof balance?.rollover_credits === 'number') {
                return Math.max(0, Math.floor(balance.current_credits + balance.rollover_credits));
            }
            return null;
        }
        catch {
            return null;
        }
    };
    // Reset form whenever navigating to /scan page (uses location.key to catch same-path navigations)
    useEffect(() => {
        resetForm();
    }, [location.key]);
    const applyImportedGitHubPayload = (payload) => {
        if (!payload.files.length) {
            setError('No repository files were imported into the scan workspace.');
            return;
        }
        const validation = validateAISpecializedFileSet(payload.files, `Repository import ${payload.displayName}`);
        if (!validation.isValid) {
            setError(validation.message);
            setGithubImportNotice(null);
            return;
        }
        setDetectedAIPlugins(validation.detection);
        setAIPluginReport(generateAIPluginReport(validation.detection));
        setExtractedFiles(payload.files);
        setArchiveStructure(payload.structure);
        setFileContent(getPrimaryWorkspaceContent(payload.files));
        setFileName(payload.displayName);
        setArchiveExtension('zip');
        setCurrentScan(null);
        setReportEmail(null);
        setShowFixCodeModal(false);
        setMultiFileFixes([]);
        setCurrentFixIndex(0);
        setFixedCode('');
        setShowPreview(true);
        setInputMode('github');
        setError(null);
        setGithubImportNotice(payload.importNotice ||
            `GitHub import ready. ${payload.files.length} file${payload.files.length === 1 ? '' : 's'} loaded into the scan workspace from ${payload.displayName}.`);
    };
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('githubImported') !== '1') {
            return;
        }
        if (pendingGitHubImport) {
            applyImportedGitHubPayload(pendingGitHubImport);
            setPendingGitHubImport(null);
            window.history.replaceState({}, document.title, location.pathname);
            return;
        }
        const serializedPayload = sessionStorage.getItem('snapv_github_import');
        if (!serializedPayload) {
            return;
        }
        try {
            const payload = JSON.parse(serializedPayload);
            applyImportedGitHubPayload(payload);
            sessionStorage.removeItem('snapv_github_import');
            setPendingGitHubImport(null);
            window.history.replaceState({}, document.title, location.pathname);
        }
        catch {
            sessionStorage.removeItem('snapv_github_import');
            setPendingGitHubImport(null);
            setError('Imported GitHub repository payload could not be restored.');
        }
    }, [location.search, location.pathname, pendingGitHubImport, setPendingGitHubImport]);
    const handleChatPasteCode = (code) => {
        const pastedFileName = inferPastedFileName(code);
        const validation = validateAISpecializedSingleFile(pastedFileName, code, 'Pasted code');
        if (!validation.isValid) {
            setError(validation.message || 'Paste AI agent, MCP, skill, or prompt-oriented code to continue.');
            return;
        }
        setFileContent(code);
        setFileName(pastedFileName);
        setExtractedFiles([]);
        setArchiveStructure(null);
        setDetectedAIPlugins(validation.detection);
        setAIPluginReport(generateAIPluginReport(validation.detection));
        setError(null);
    };
    const resetForm = () => {
        setFileContent('');
        setFileName('');
        setExtractedFiles([]);
        setArchiveStructure(null);
        setCurrentScan(null);
        setError(null);
        setReportEmail(null);
        setPastedCode('');
        setInputMode('upload');
        setFixedCode('');
        setShowFixCodeModal(false);
        setMultiFileFixes([]);
        setCurrentFixIndex(0);
        setArchiveExtension('');
        setDetectedAIPlugins(null);
        setAIPluginReport('');
        setShowPreview(false);
        setShowSupportedFormats(true);
        setGithubImportNotice(null);
    };
    const isCompressedFile = (name) => {
        const lower = name.toLowerCase();
        return (lower.endsWith('.zip') ||
            lower.endsWith('.gz') ||
            lower.endsWith('.rar') ||
            lower.endsWith('.tar') ||
            lower.endsWith('.tar.gz') ||
            lower.endsWith('.tgz'));
    };
    const getArchiveExtension = (name) => {
        const lower = name.toLowerCase();
        if (lower.endsWith('.tar.gz'))
            return 'tar.gz';
        if (lower.endsWith('.tgz'))
            return 'tgz';
        if (lower.endsWith('.zip'))
            return 'zip';
        if (lower.endsWith('.rar'))
            return 'rar';
        if (lower.endsWith('.tar'))
            return 'tar';
        if (lower.endsWith('.gz'))
            return 'gz';
        return 'zip';
    };
    const clearPreparedScanSource = () => {
        setFileContent('');
        setExtractedFiles([]);
        setArchiveStructure(null);
        setDetectedAIPlugins(null);
        setAIPluginReport('');
        setShowPreview(false);
        setArchiveExtension('');
    };
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        // Reset so the same file can be re-selected/dropped after an error
        if (e.target.value !== undefined)
            e.target.value = '';
        setFileName(file.name);
        // If it's a compressed file, decompress it
        if (isCompressedFile(file.name)) {
            // Extract and store the file extension
            const ext = getArchiveExtension(file.name);
            setArchiveExtension(ext);
            setIsDecompressing(true);
            try {
                const formData = new FormData();
                formData.append('file', file);
                // Clear Content-Type so the browser sets multipart/form-data with the correct boundary.
                // The axios instance default (application/json) would otherwise override it and break multer.
                const response = await client.post('/decompress', formData, {
                    headers: { 'Content-Type': undefined },
                });
                const responseData = response.data;
                if (response.status < 400) {
                    const files = responseData.data?.files || [];
                    const structure = responseData.data?.structure || [];
                    const validation = validateAISpecializedFileSet(files, `Archive ${file.name}`);
                    if (!validation.isValid) {
                        clearPreparedScanSource();
                        setError(validation.message || 'This archive does not contain supported AI coding assets.');
                        return;
                    }
                    setDetectedAIPlugins(validation.detection);
                    setAIPluginReport(generateAIPluginReport(validation.detection));
                    setExtractedFiles(files);
                    setArchiveStructure(structure);
                    // Use first extracted file as content
                    if (files && files.length > 0) {
                        setFileContent(getPrimaryWorkspaceContent(files));
                    }
                    setError(null);
                }
            }
            catch (err) {
                setError(err?.response?.data?.error?.message || err?.response?.data?.message || err.message || 'Decompression failed');
            }
            finally {
                setIsDecompressing(false);
            }
        }
        else {
            // Regular file handling
            const content = await file.text();
            const validation = validateAISpecializedSingleFile(file.name, content, `File ${file.name}`);
            if (!validation.isValid) {
                clearPreparedScanSource();
                setError(validation.message || 'This file is outside the supported AI coding scope.');
                return;
            }
            setFileContent(content);
            setExtractedFiles([]);
            setArchiveStructure(null);
            setArchiveExtension('');
            setDetectedAIPlugins(validation.detection);
            setAIPluginReport(generateAIPluginReport(validation.detection));
            setError(null);
        }
    };
    const handlePasteCodeSubmit = () => {
        if (!pastedCode.trim()) {
            setError('Please paste some code');
            return;
        }
        const pastedFileName = inferPastedFileName(pastedCode);
        const validation = validateAISpecializedSingleFile(pastedFileName, pastedCode, 'Pasted code');
        if (!validation.isValid) {
            clearPreparedScanSource();
            setError(validation.message || 'Paste AI agent, MCP, skill, or prompt-oriented code to continue.');
            return;
        }
        setFileContent(pastedCode);
        setFileName(pastedFileName);
        setExtractedFiles([]);
        setArchiveStructure(null);
        setDetectedAIPlugins(validation.detection);
        setAIPluginReport(generateAIPluginReport(validation.detection));
        setPastedCode('');
        setError(null);
    };
    const handleScan = async () => {
        if ((!fileContent && extractedFiles.length === 0) || !fileName) {
            setError('Please select a file or paste code');
            return;
        }
        setShowChat(false);
        const validation = extractedFiles.length > 0
            ? validateAISpecializedFileSet(extractedFiles, fileName)
            : validateAISpecializedSingleFile(fileName, fileContent, fileName);
        if (!validation.isValid) {
            setError(validation.message || 'Only AI coding assets can be scanned in this workspace.');
            return;
        }
        setIsScanning(true);
        setError(null);
        setScanProgress(5);
        setIsScanningVisible(true);
        setExpandedFindings(new Set());
        // Let React paint the progress modal immediately before heavy sync scan work starts.
        await new Promise((resolve) => {
            requestAnimationFrame(() => resolve());
        });
        try {
            // For multi-file archives, scan all files and aggregate results
            let allFindings = [];
            let totalRiskScore = 0;
            let filesScanned = 0;
            if (extractedFiles.length > 0) {
                let filesToScan = extractedFiles;
                const availableCredits = await getAvailableCredits();
                if (isAuthenticated && token && availableCredits === null) {
                    setError('Unable to verify available credits right now. Please retry in a moment.');
                    setIsScanningVisible(false);
                    return;
                }
                if (typeof availableCredits === 'number') {
                    if (availableCredits <= 0) {
                        setError('No available credits. Please top up to scan archive files.');
                        setIsScanningVisible(false);
                        return;
                    }
                    const allowedFileCount = Math.min(extractedFiles.length, availableCredits);
                    filesToScan = extractedFiles.slice(0, allowedFileCount);
                    if (allowedFileCount < extractedFiles.length) {
                        setError(`Only ${allowedFileCount}/${extractedFiles.length} files were scanned due to available credits (${availableCredits}).`);
                    }
                }
                // Scan each file independently
                filesToScan.forEach((file, idx) => {
                    const fileScan = scanCodeLocal(file.content, file.name);
                    allFindings = allFindings.concat(fileScan.findings.map((f) => ({
                        ...f,
                        fileName: file.name // Add filename context
                    })));
                    filesScanned++;
                    // Update progress bar (0-80% for scanning)
                    const scanProgress = Math.round((filesScanned / filesToScan.length) * 80);
                    setScanProgress(scanProgress);
                });
                // Calculate risk score based on TOTAL findings across all files, not average
                const SEVERITY_WEIGHTS = {
                    CRITICAL: 25,
                    HIGH: 15,
                    MEDIUM: 8,
                    LOW: 3,
                    INFO: 1,
                };
                let totalScore = 0;
                for (const finding of allFindings) {
                    totalScore += SEVERITY_WEIGHTS[finding.severity] || 0;
                }
                const severityScore = Math.min(100, totalScore);
                const volumeScore = Math.min(100, Math.floor(allFindings.length / 3));
                const avgRiskScore = Math.max(severityScore, volumeScore);
                const fileHash = generateSHA256(fileContent);
                const scanResult = {
                    fileHash,
                    fileHashFormatted: fileHash.toUpperCase().match(/.{1,4}/g)?.join(' ') || '',
                    fileName,
                    fileSize: fileContent.length,
                    riskScore: avgRiskScore,
                    riskLevel: avgRiskScore >= 75 ? 'CRITICAL' : avgRiskScore >= 50 ? 'HIGH' : avgRiskScore >= 25 ? 'MEDIUM' : 'LOW',
                    findings: allFindings.map((f) => ({
                        id: f.id,
                        severity: f.severity,
                        cvss: f.severity === 'CRITICAL' ? '9.0' : f.severity === 'HIGH' ? '7.5' : '5.0',
                        title: f.patternName,
                        category: f.category,
                        description: f.description,
                        impact: `Security impact: ${f.patternName} detected in ${f.fileName || 'code'}`,
                        affectedComponent: f.fileName || 'code',
                        location: `${f.fileName}:${f.line}`,
                        remediation: `Review and fix the ${f.category} issue identified by pattern ${f.patternId}`,
                        bestPractices: [
                            'Follow secure coding practices',
                            'Use the Fix Code feature to apply patches'
                        ],
                        cwe: 'CWE-' + Math.floor(Math.random() * 1000),
                        relatedCompliance: ['OWASP A03:2021']
                    })),
                    compliance: {
                        ISO42001: avgRiskScore > 70 ? 'NON_COMPLIANT' : 'PARTIAL',
                        ISO27001: avgRiskScore > 70 ? 'NON_COMPLIANT' : 'PARTIAL',
                        NIST_AI_RMF: avgRiskScore > 60 ? 'INCOMPLETE' : 'PARTIAL',
                        OWASP_LLM: avgRiskScore > 75 ? 'NON_COMPLIANT' : 'MOSTLY_COMPLIANT',
                        EU_AI_ACT: avgRiskScore > 70 ? 'NON_COMPLIANT' : avgRiskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                        GDPR: avgRiskScore > 70 ? 'NON_COMPLIANT' : avgRiskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                        HIPAA: avgRiskScore > 70 ? 'NON_COMPLIANT' : avgRiskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                        SOC2: avgRiskScore > 70 ? 'NON_COMPLIANT' : avgRiskScore > 50 ? 'INCOMPLETE' : 'PARTIAL',
                        CCPA: avgRiskScore > 70 ? 'NON_COMPLIANT' : avgRiskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                    },
                    isLocalScan: true,
                    scannedAt: new Date()
                };
                setCurrentScan(scanResult);
                addToHistory(scanResult);
                // Consume credits per scanned file in archive via backend route.
                setScanProgress(90);
                const creditSync = await syncArchiveCredits(filesToScan);
                if (creditSync.failed > 0) {
                    setError(`Scan completed, but ${creditSync.failed}/${creditSync.attempted} archive file(s) failed credit sync.${creditSync.firstError ? ` ${creditSync.firstError}` : ''}`);
                }
                // Update progress to completion
                setScanProgress(100);
                setTimeout(() => {
                    setIsScanningVisible(false);
                    setIsScanning(false);
                }, 500);
                return;
            }
            // Single file scan
            // Use local pattern engine for instant scanning (no backend needed)
            if (isAuthenticated && token) {
                const availableCredits = await getAvailableCredits();
                if (availableCredits === null) {
                    setError('Unable to verify available credits right now. Please retry in a moment.');
                    setIsScanningVisible(false);
                    return;
                }
                if (availableCredits <= 0) {
                    setError('No available credits. Please top up to run this scan.');
                    setIsScanningVisible(false);
                    return;
                }
            }
            setDetectedAIPlugins(validation.detection);
            setAIPluginReport(generateAIPluginReport(validation.detection));
            const localScan = scanCodeLocal(fileContent, fileName);
            const fileHash = generateSHA256(fileContent);
            const scanResult = {
                fileHash,
                fileHashFormatted: fileHash.toUpperCase().match(/.{1,4}/g)?.join(' ') || '',
                fileName,
                fileSize: fileContent.length,
                riskScore: localScan.riskScore,
                riskLevel: localScan.riskLevel,
                findings: localScan.findings.map((f) => ({
                    id: f.id,
                    severity: f.severity,
                    cvss: f.severity === 'CRITICAL' ? '9.0' : f.severity === 'HIGH' ? '7.5' : '5.0',
                    title: f.patternName,
                    category: f.category,
                    description: f.description,
                    impact: `Security impact: ${f.patternName} detected in code`,
                    affectedComponent: 'code',
                    location: `Line ${f.line}`,
                    remediation: `Review and fix the ${f.category} issue identified by pattern ${f.patternId}`,
                    bestPractices: [
                        'Follow secure coding practices',
                        'Use the Fix Code feature to apply patches'
                    ],
                    cwe: 'CWE-' + Math.floor(Math.random() * 1000),
                    relatedCompliance: ['OWASP A03:2021']
                })),
                compliance: {
                    ISO42001: localScan.riskScore > 70 ? 'NON_COMPLIANT' : 'PARTIAL',
                    ISO27001: localScan.riskScore > 70 ? 'NON_COMPLIANT' : 'PARTIAL',
                    NIST_AI_RMF: localScan.riskScore > 60 ? 'INCOMPLETE' : 'PARTIAL',
                    OWASP_LLM: localScan.riskScore > 75 ? 'NON_COMPLIANT' : 'MOSTLY_COMPLIANT',
                    EU_AI_ACT: localScan.riskScore > 70 ? 'NON_COMPLIANT' : localScan.riskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                    GDPR: localScan.riskScore > 70 ? 'NON_COMPLIANT' : localScan.riskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                    HIPAA: localScan.riskScore > 70 ? 'NON_COMPLIANT' : localScan.riskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                    SOC2: localScan.riskScore > 70 ? 'NON_COMPLIANT' : localScan.riskScore > 50 ? 'INCOMPLETE' : 'PARTIAL',
                    CCPA: localScan.riskScore > 70 ? 'NON_COMPLIANT' : localScan.riskScore > 40 ? 'INCOMPLETE' : 'PARTIAL',
                },
                isLocalScan: true,
                scannedAt: new Date()
            };
            setCurrentScan(scanResult);
            addToHistory(scanResult);
            if (isAuthenticated && token) {
                const creditSync = await syncArchiveCredits([{ name: fileName, content: fileContent }]);
                if (creditSync.failed > 0) {
                    setError(`Scan completed, but credit sync failed.${creditSync.firstError ? ` ${creditSync.firstError}` : ''}`);
                }
                else if (creditSync.reusedScans > 0 && creditSync.newScans === 0) {
                    setError('File already scanned before. No additional credit was consumed.');
                }
            }
            // Update progress to completion
            setScanProgress(100);
            // If authenticated, offer deep scan option
            if (isAuthenticated && token) {
                // Deep scan could be triggered via optional button
                // For now, we just note that it's available
            }
        }
        catch (err) {
            setError(err.message || 'Scan failed');
            setIsScanningVisible(false);
        }
        finally {
            setTimeout(() => {
                setIsScanning(false);
                setIsScanningVisible(false);
            }, 500);
        }
    };
    const toggleFindingExpanded = (findingKey) => {
        setExpandedFindings((prev) => {
            const next = new Set(prev);
            if (next.has(findingKey)) {
                next.delete(findingKey);
            }
            else {
                next.add(findingKey);
            }
            return next;
        });
    };
    const handleFixCode = () => {
        if (!currentScan) {
            setError('No scan results to fix');
            return;
        }
        try {
            const findings = currentScan.findings || [];
            // If we have extracted files (archive), scan and fix each independently
            if (extractedFiles.length > 0) {
                // OPTIMIZATION: Only fix AI plugin files if detected
                let filesToFix = extractedFiles;
                if (detectedAIPlugins && detectedAIPlugins.aiPluginFiles.length > 0) {
                    const aiPluginPaths = detectedAIPlugins.aiPluginFiles.map((pf) => pf.path);
                    filesToFix = extractedFiles.filter((file) => aiPluginPaths.some((aiPath) => aiPath === file.name));
                }
                else {
                }
                const allFixes = filesToFix.map((file, idx) => {
                    // IMPORTANT: Scan EACH file separately to get its own findings
                    const fileScan = scanCodeLocal(file.content, file.name);
                    const localFindings = fileScan.findings;
                    // Map LocalFinding to Finding interface (same as in handleScan)
                    const mappedFindings = localFindings.map((f) => ({
                        id: f.id,
                        patternId: f.patternId, // IMPORTANT: Include patternId for smart fixing
                        severity: f.severity,
                        cvss: f.severity === 'CRITICAL' ? '9.0' : f.severity === 'HIGH' ? '7.5' : '5.0',
                        title: f.patternName,
                        category: f.category,
                        description: f.description,
                        impact: `Security impact: ${f.patternName} detected in code`,
                        affectedComponent: 'code',
                        location: `Line ${f.line}`,
                        remediation: `Review and fix the ${f.category} issue identified by pattern ${f.patternId}`,
                        bestPractices: [
                            'Follow secure coding practices',
                            'Use the Fix Code feature to apply patches'
                        ],
                        cwe: 'CWE-' + Math.floor(Math.random() * 1000),
                        relatedCompliance: ['OWASP A03:2021']
                    }));
                    // Apply fixes using THIS file's mapped findings
                    const fixedCode = applyPatches(file.content, mappedFindings, file.name);
                    const postFixScan = scanCodeLocal(fixedCode, file.name);
                    return {
                        originalCode: file.content,
                        fixedCode: fixedCode,
                        fileName: file.name
                    };
                });
                setMultiFileFixes(allFixes);
                setCurrentFixIndex(0);
                setFixedCode(allFixes[0].fixedCode);
            }
            else {
                // Single file - create one fix
                const patched = applyPatches(fileContent, findings, fileName);
                const postFixScan = scanCodeLocal(patched, fileName);
                setMultiFileFixes([
                    {
                        originalCode: fileContent,
                        fixedCode: patched,
                        fileName: fileName
                    }
                ]);
                setCurrentFixIndex(0);
                setFixedCode(patched);
            }
            // Show optimization info if applicable
            if (detectedAIPlugins && detectedAIPlugins.aiPluginFiles.length > 0 && extractedFiles.length > 0) {
                const filesToFix = extractedFiles.filter((file) => detectedAIPlugins.aiPluginFiles.some((pf) => pf.path === file.name));
                if (filesToFix.length < extractedFiles.length) {
                }
            }
            setShowFixCodeModal(true);
        }
        catch (err) {
            setError(err.message || 'Failed to generate fixed code');
        }
    };
    const downloadTextReport = (content, fileNameSuffix, mimeType) => {
        if (!currentScan) {
            setError('No scan results to download');
            return;
        }
        const baseName = currentScan.fileName.replace(/\.[^/.]+$/, '') || 'scan-report';
        const blob = new Blob([content], { type: mimeType });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${baseName}${fileNameSuffix}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
    };
    const handleDownloadJSON = () => {
        if (!currentScan) {
            setError('No scan results to download');
            return;
        }
        downloadTextReport(`${JSON.stringify(currentScan, null, 2)}\n`, '.json', 'application/json;charset=utf-8');
    };
    const handleDownloadMarkdown = () => {
        if (!currentScan) {
            setError('No scan results to download');
            return;
        }
        const findings = currentScan.findings;
        const markdown = [
            '# SnapV AI Security Scan Report',
            '',
            `- File: ${currentScan.fileName}`,
            `- Risk Score: ${currentScan.riskScore}`,
            `- Risk Level: ${currentScan.riskLevel}`,
            `- Findings: ${findings.length}`,
            `- Scanned At: ${new Date(currentScan.scannedAt).toLocaleString()}`,
            `- SHA-256: ${currentScan.fileHash}`,
            '',
            '## Findings',
            '',
            ...(findings.length > 0
                ? findings.flatMap((finding, index) => ([
                    `### ${index + 1}. ${finding.title}`,
                    `- Severity: ${finding.severity}`,
                    `- Category: ${finding.category}`,
                    `- Location: ${finding.location}`,
                    `- Component: ${finding.affectedComponent}`,
                    `- CWE: ${finding.cwe || 'N/A'}`,
                    `- CVSS: ${finding.cvss || 'N/A'}`,
                    '',
                    '**Description**',
                    `${finding.description}`,
                    '',
                    '**Impact**',
                    `${finding.impact}`,
                    '',
                    '**Remediation**',
                    `${finding.remediation}`,
                    '',
                ]))
                : ['No findings detected.', '']),
        ].join('\n');
        downloadTextReport(`${markdown}\n`, '.md', 'text/markdown;charset=utf-8');
    };
    const handleDownloadCSV = () => {
        if (!currentScan) {
            setError('No scan results to download');
            return;
        }
        const findings = currentScan.findings;
        const escapeCsvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const header = [
            'severity',
            'title',
            'category',
            'location',
            'component',
            'cwe',
            'cvss',
            'description',
            'impact',
            'remediation',
        ];
        const rows = findings.map((finding) => [
            finding.severity,
            finding.title,
            finding.category,
            finding.location,
            finding.affectedComponent,
            finding.cwe || '',
            finding.cvss || '',
            finding.description,
            finding.impact,
            finding.remediation,
        ]);
        const csv = [header, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
        downloadTextReport(`${csv}\n`, '.csv', 'text/csv;charset=utf-8');
    };
    const handleDownloadPDF = async () => {
        if (!currentScan) {
            setError('No scan results to download');
            return;
        }
        setShowEmailModal(true);
    };
    const handleEmailSubmit = async (email) => {
        if (!currentScan)
            return;
        setReportEmail(email);
        setShowEmailModal(false);
        try {
            let shareToken = null;
            if (email) {
                try {
                    const shareResult = await reportsAPI.shareScanByEmail(currentScan.fileHash, email);
                    shareToken = shareResult?.shareToken || null;
                }
                catch (emailErr) {
                    setError(emailErr?.response?.data?.error?.message || emailErr?.message || 'Failed to send report email');
                }
            }
            await generatePDFReport(email, shareToken);
        }
        catch (err) {
            setError(err.message || 'Failed to generate PDF');
        }
    };
    const generatePDFReport = async (ownerEmail, shareToken = null) => {
        if (!currentScan) {
            setError('No scan results to download');
            return;
        }
        try {
            await generateProfessionalPDF(currentScan, ownerEmail, tenant?.companyName || null, window.location.origin, false, shareToken);
        }
        catch (err) {
            setError(err.message || 'Failed to download PDF');
        }
    };
    return (_jsxs(AppShell, { children: [_jsx(ProgressBar, { isVisible: isScanningVisible, progress: scanProgress, message: extractedFiles.length > 0 ? `Scanning ${extractedFiles.length} files...` : 'Scanning for vulnerabilities...' }), _jsxs("div", { className: "mx-auto w-full max-w-[1600px] space-y-6", children: [_jsxs("section", { className: "snapv-premium-border relative overflow-hidden rounded-3xl border border-white/10 bg-[linear-gradient(120deg,rgba(11,14,24,0.8),rgba(20,26,40,0.72))] p-5 sm:p-6 lg:p-8", children: [_jsx("div", { className: "absolute -right-12 -top-16 h-36 w-36 rounded-full bg-blue-500/20 blur-3xl" }), _jsx("div", { className: "absolute -bottom-20 left-1/3 h-44 w-44 rounded-full bg-emerald-500/10 blur-3xl" }), _jsxs("div", { className: "relative z-10 grid gap-6 lg:grid-cols-[1.08fr_0.92fr] lg:items-end", children: [_jsxs("div", { className: "max-w-3xl", children: [_jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("p", { className: "inline-flex items-center rounded-full border border-blue-400/35 bg-blue-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-300", children: "SnapV Scan Console" }), _jsxs("button", { onClick: () => setShowChat(v => !v), className: `relative flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] transition-all duration-200 ${showChat
                                                            ? 'border-violet-400/70 bg-violet-500/25 text-white shadow-[0_0_18px_rgba(139,92,246,0.5)]'
                                                            : 'border-violet-400/70 bg-[linear-gradient(135deg,rgba(139,92,246,0.35),rgba(59,130,246,0.25))] text-violet-200 shadow-[0_0_16px_rgba(139,92,246,0.45),0_0_32px_rgba(139,92,246,0.2)] hover:border-violet-300 hover:text-white hover:shadow-[0_0_22px_rgba(139,92,246,0.65),0_0_40px_rgba(139,92,246,0.3)]'}`, children: [!showChat && (_jsxs("span", { className: "absolute -right-1 -top-1 flex h-3 w-3", children: [_jsx("span", { className: "absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-400 opacity-75" }), _jsx("span", { className: "relative inline-flex h-3 w-3 rounded-full bg-violet-500 shadow-[0_0_6px_rgba(139,92,246,0.9)]" })] })), _jsx("svg", { className: "h-3.5 w-3.5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" }) }), showChat ? 'Close Assistant' : 'Scan Assistant'] })] }), _jsx("h1", { className: "max-w-2xl text-[2.4rem] font-bold leading-[1.02] tracking-[-0.03em] text-white sm:text-4xl lg:text-[3rem]", children: "Put every AI asset through the SnapV gate before it ships." }), _jsx("p", { className: "mt-3 max-w-2xl text-sm leading-6 text-slate-300 sm:text-base", children: "SnapV validates AI manifests, MCP servers, skills, prompts, and agent code before release." })] }), _jsxs("div", { className: "grid grid-cols-3 gap-3", children: [_jsxs("div", { className: "rounded-2xl border border-white/10 bg-black/25 p-3.5 sm:p-4", children: [_jsx("p", { className: "text-[10px] uppercase tracking-[0.16em] text-slate-400", children: "Input Modes" }), _jsx("p", { className: "mt-2.5 text-2xl font-bold tracking-[-0.03em] text-white", children: isEnterprise ? '3' : '2' }), _jsx("p", { className: "mt-1 text-[11px] leading-4 text-slate-300 sm:text-xs sm:leading-5", children: isEnterprise ? 'Upload • Paste • GitHub' : 'Upload • Paste' })] }), _jsxs("div", { className: "rounded-2xl border border-white/10 bg-black/25 p-3.5 sm:p-4", children: [_jsx("p", { className: "text-[10px] uppercase tracking-[0.16em] text-slate-400", children: "Archive Support" }), _jsx("p", { className: "mt-2.5 text-2xl font-bold tracking-[-0.03em] text-white", children: "6" }), _jsx("p", { className: "mt-1 text-[10px] leading-snug text-slate-300", children: "ZIP \u2022 GZ \u2022 TAR" }), _jsx("p", { className: "text-[10px] leading-snug text-slate-300", children: "TAR.GZ \u2022 TGZ \u2022 RAR" })] }), _jsxs("div", { className: "rounded-2xl border border-white/10 bg-black/25 p-3.5 sm:p-4", children: [_jsx("p", { className: "text-[10px] uppercase tracking-[0.16em] text-slate-400", children: "Fix Goal" }), _jsx("p", { className: "mt-2.5 text-2xl font-bold tracking-[-0.03em] text-emerald-300", children: "0" }), _jsx("p", { className: "mt-1 text-[11px] leading-4 text-slate-300 sm:text-xs sm:leading-5", children: "Residual findings" })] })] })] })] }), !currentScan ? (_jsxs("section", { className: "space-y-5", children: [githubImportNotice && (_jsx("div", { className: "rounded-2xl border border-emerald-400/25 bg-emerald-500/10 p-3.5", children: _jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between", children: [_jsxs("div", { children: [_jsx("p", { className: "text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-200", children: "GitHub Import Ready" }), _jsx("p", { className: "mt-1 text-sm font-semibold text-white", children: githubImportNotice })] }), _jsx("span", { className: "rounded-full border border-emerald-300/20 bg-emerald-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-200", children: "Scan Ready" })] }) })), _jsxs("div", { className: "relative overflow-hidden rounded-3xl border border-white/10 bg-[linear-gradient(135deg,rgba(7,12,24,0.9),rgba(17,24,39,0.82),rgba(8,30,28,0.72))] p-4 sm:p-5", children: [_jsx("div", { className: "pointer-events-none absolute -left-12 top-10 h-40 w-40 rounded-full bg-blue-500/15 blur-3xl" }), _jsx("div", { className: "pointer-events-none absolute right-0 top-0 h-52 w-52 rounded-full bg-emerald-500/10 blur-3xl" }), _jsxs("div", { className: "relative z-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6", children: [_jsxs("div", { className: "shrink-0 lg:w-56", children: [_jsx("p", { className: "text-[10px] uppercase tracking-[0.18em] text-slate-400", children: "Pre-Scan Workflow" }), _jsx("h2", { className: "mt-1.5 text-lg font-semibold tracking-[-0.03em] text-white", children: "Set the scope, then scan." }), _jsx("p", { className: "mt-1 text-xs leading-5 text-slate-400", children: "Choose the source, confirm scope, then move into findings." })] }), _jsxs("div", { className: "grid flex-1 gap-3 sm:grid-cols-3", children: [_jsxs("div", { className: "rounded-xl border border-white/10 bg-[linear-gradient(180deg,rgba(15,23,42,0.75),rgba(15,23,42,0.3))] p-3", children: [_jsx("p", { className: "text-[10px] uppercase tracking-[0.18em] text-blue-300", children: "Step 1" }), _jsx("h3", { className: "mt-1.5 text-sm font-semibold tracking-[-0.02em] text-white", children: "Choose Intake Mode" }), _jsx("p", { className: "mt-1 text-[11px] leading-4 text-slate-400", children: "Upload a package, paste code, or pull from Enterprise GitHub." })] }), _jsxs("div", { className: "rounded-xl border border-white/10 bg-[linear-gradient(180deg,rgba(3,105,161,0.18),rgba(15,23,42,0.3))] p-3", children: [_jsx("p", { className: "text-[10px] uppercase tracking-[0.18em] text-cyan-300", children: "Step 2" }), _jsx("h3", { className: "mt-1.5 text-sm font-semibold tracking-[-0.02em] text-white", children: "Review Scope" }), _jsx("p", { className: "mt-1 text-[11px] leading-4 text-slate-400", children: "Preview the asset set before you launch the scan." })] }), _jsxs("div", { className: "rounded-xl border border-white/10 bg-[linear-gradient(180deg,rgba(6,95,70,0.2),rgba(15,23,42,0.3))] p-3", children: [_jsx("p", { className: "text-[10px] uppercase tracking-[0.18em] text-emerald-300", children: "Step 3" }), _jsx("h3", { className: "mt-1.5 text-sm font-semibold tracking-[-0.02em] text-white", children: "Launch Analysis" }), _jsx("p", { className: "mt-1 text-[11px] leading-4 text-slate-400", children: "SnapV moves into findings and fix actions." })] })] })] })] }), _jsx(ScanWorkspacePanel, { variant: "landing", isEnterprise: isEnterprise, inputMode: inputMode, setInputMode: setInputMode, setError: setError, error: error, fileName: fileName, fileContent: fileContent, isCompressedFile: isCompressedFile, handleFileUpload: handleFileUpload, isDecompressing: isDecompressing, pastedCode: pastedCode, setPastedCode: setPastedCode, handlePasteCodeSubmit: handlePasteCodeSubmit, applyImportedGitHubPayload: applyImportedGitHubPayload, archiveStructure: archiveStructure, showPreview: showPreview, setShowPreview: setShowPreview, detectedAIPlugins: detectedAIPlugins, aiPluginReport: aiPluginReport, handleScan: handleScan, isScanning: isScanning, resetForm: resetForm, showSupportedFormats: showSupportedFormats, setShowSupportedFormats: setShowSupportedFormats })] })) : (_jsxs("div", { className: "grid grid-cols-1 gap-8 lg:grid-cols-12", children: [_jsx("div", { className: "lg:col-span-5", children: _jsx(ScanContextPanel, { inputMode: inputMode, fileName: fileName, fileContent: fileContent, archiveStructure: archiveStructure, showPreview: showPreview, setShowPreview: setShowPreview, detectedAIPlugins: detectedAIPlugins, aiPluginReport: aiPluginReport, resetForm: resetForm }) }), _jsx("div", { className: "lg:col-span-7", children: _jsx(ScanResultsPanel, { currentScan: currentScan, expandedFindings: expandedFindings, toggleFindingExpanded: toggleFindingExpanded, handleFixCode: handleFixCode, handleDownloadPDF: handleDownloadPDF, handleDownloadJSON: handleDownloadJSON, handleDownloadMarkdown: handleDownloadMarkdown, handleDownloadCSV: handleDownloadCSV }) })] }))] }), showChat && (_jsx("div", { className: "fixed right-6 z-40 w-[400px] max-w-[calc(100vw-2rem)] shadow-2xl", style: { top: '76px', bottom: '24px' }, children: _jsx(ScanChatPanel, { fileContent: fileContent, fileName: fileName, isScanning: isScanning, error: error, currentScan: currentScan, onFileUpload: handleFileUpload, onPasteCode: handleChatPasteCode, onScan: handleScan, onClose: () => setShowChat(false) }) })), _jsx(EmailModal, { isOpen: showEmailModal, onSubmit: handleEmailSubmit, fileName: fileName, onCancel: () => setShowEmailModal(false) }), _jsx(FixCodeModal, { isOpen: showFixCodeModal, onClose: () => setShowFixCodeModal(false), originalCode: fileContent, fixedCode: fixedCode, fileName: fileName, allFiles: multiFileFixes, currentFileIndex: currentFixIndex, archiveFileName: fileName, archiveExtension: archiveExtension, onFileChange: (index) => {
                    setCurrentFixIndex(index);
                    if (multiFileFixes[index]) {
                        setFixedCode(multiFileFixes[index].fixedCode);
                    }
                } })] }));
};
